/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 *
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useEffect, useRef, useState } from 'react';
import { formatCurrency } from '../utils/formatters.ts';
import './SalesTrendChart.css';

export interface TrendPoint {
  label: string; // short axis label, e.g. "Oct 3"
  fullLabel: string; // tooltip label, e.g. "Oct 3, 2026"
  sales: number;
  orders: number;
}

interface TrendPanelProps {
  title: string;
  points: TrendPoint[];
  metric: 'sales' | 'orders';
  colorClass: string;
  formatValue: (value: number) => string;
  rangeLabel: string; // e.g. "Last 30 Days" - labels the default (not-hovering) total
}

// A single small-multiple trend panel - one series, one unit. Sales ($)
// and order count (#) are different scales, so per the dual-axis rule
// they're always two of these side by side rather than one chart with two
// y-axes.
const TrendPanel: React.FC<TrendPanelProps> = ({ title, points, metric, colorClass, formatValue, rangeLabel }) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);

  const width = 600;
  const height = 140;
  const paddingTop = 16;
  const paddingBottom = 24;
  const paddingX = 4;
  const plotHeight = height - paddingTop - paddingBottom;
  const plotWidth = width - paddingX * 2;

  const values = points.map((p) => p[metric]);
  const maxValue = Math.max(1, ...values);
  // Shown in the header when nothing is hovered: the total for the whole
  // window rather than just the latest day (which is usually $0 / 0 early
  // in the day and isn't very informative).
  const rangeTotal = values.reduce((sum, v) => sum + v, 0);

  const n = points.length;
  const xFor = (i: number) => (n <= 1 ? paddingX : paddingX + (i / (n - 1)) * plotWidth);
  const yFor = (v: number) => paddingTop + plotHeight - (v / maxValue) * plotHeight;

  const linePath = points
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(i).toFixed(2)} ${yFor(p[metric]).toFixed(2)}`)
    .join(' ');

  const areaPath =
    n > 0
      ? `${linePath} L ${xFor(n - 1).toFixed(2)} ${(paddingTop + plotHeight).toFixed(2)} ` +
        `L ${xFor(0).toFixed(2)} ${(paddingTop + plotHeight).toFixed(2)} Z`
      : '';

  const panelRef = useRef<HTMLDivElement>(null);
  const isTouchDragging = useRef(false);

  const indexFromEvent = (event: React.PointerEvent<SVGRectElement>): number => {
    const rect = event.currentTarget.getBoundingClientRect();
    const fraction = (event.clientX - rect.left) / rect.width;
    const index = Math.round(fraction * (n - 1));
    return Math.max(0, Math.min(n - 1, index));
  };

  // Mouse: hover to preview, cleared when the pointer leaves.
  // Touch/pen: tap (or drag sideways to scrub) to pick a day; the details
  // stay up after lifting the finger, and a tap outside the chart clears
  // them. Vertical swipes still scroll the page (touch-action: pan-y).
  const handlePointerDown = (event: React.PointerEvent<SVGRectElement>) => {
    if (n === 0 || event.pointerType === 'mouse') return;
    isTouchDragging.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    setHoverIndex(indexFromEvent(event));
  };

  const handlePointerMove = (event: React.PointerEvent<SVGRectElement>) => {
    if (n === 0) return;
    if (event.pointerType === 'mouse' || isTouchDragging.current) {
      setHoverIndex(indexFromEvent(event));
    }
  };

  const endTouchDrag = () => {
    isTouchDragging.current = false;
  };

  const handlePointerLeave = (event: React.PointerEvent<SVGRectElement>) => {
    if (event.pointerType === 'mouse') setHoverIndex(null);
  };

  // Dismiss a touch selection when the user taps anywhere outside this panel.
  useEffect(() => {
    if (hoverIndex === null) return;
    const handleOutside = (event: PointerEvent) => {
      if (event.pointerType === 'mouse') return;
      if (panelRef.current && !panelRef.current.contains(event.target as Node)) {
        setHoverIndex(null);
      }
    };
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, [hoverIndex]);

  const hovered = hoverIndex !== null ? points[hoverIndex] : null;

  return (
    <div className={`trend-panel ${colorClass}`} ref={panelRef}>
      <div className="trend-panel__header">
        <span className="trend-panel__title">{title}</span>
        {hovered ? (
          <span className="trend-panel__hover-value">
            {formatValue(hovered[metric])} <span className="trend-panel__hover-date">{hovered.fullLabel}</span>
          </span>
        ) : (
          <span className="trend-panel__hover-value trend-panel__hover-value--muted">
            {formatValue(rangeTotal)} <span className="trend-panel__hover-date">{rangeLabel.toLowerCase()}</span>
          </span>
        )}
      </div>

      <svg viewBox={`0 0 ${width} ${height}`} className="trend-panel__svg" preserveAspectRatio="none">
        {n > 1 && <path d={areaPath} className="trend-panel__area" />}
        {n > 1 && <path d={linePath} className="trend-panel__line" />}
        {n === 1 && (
          <circle cx={xFor(0)} cy={yFor(points[0][metric])} r={4} className="trend-panel__dot-single" />
        )}

        {hovered && hoverIndex !== null && (
          <>
            <line
              x1={xFor(hoverIndex)}
              x2={xFor(hoverIndex)}
              y1={paddingTop}
              y2={paddingTop + plotHeight}
              className="trend-panel__crosshair"
            />
            <circle
              cx={xFor(hoverIndex)}
              cy={yFor(hovered[metric])}
              r={4}
              className="trend-panel__dot"
            />
          </>
        )}

        {n > 0 && (
          <rect
            x={0}
            y={0}
            width={width}
            height={height}
            className="trend-panel__hit-area"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endTouchDrag}
            onPointerCancel={endTouchDrag}
            onPointerLeave={handlePointerLeave}
          />
        )}
      </svg>

      <div className="trend-panel__axis">
        <span>{points[0]?.label}</span>
        <span>{points[n - 1]?.label}</span>
      </div>

      <table className="sr-only">
        <caption>{title} by date</caption>
        <thead>
          <tr>
            <th>Date</th>
            <th>{title}</th>
          </tr>
        </thead>
        <tbody>
          {points.map((p) => (
            <tr key={p.fullLabel}>
              <td>{p.fullLabel}</td>
              <td>{formatValue(p[metric])}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface SalesTrendChartProps {
  points: TrendPoint[];
  rangeLabel: string;
  loading?: boolean;
}

const SalesTrendChart: React.FC<SalesTrendChartProps> = ({ points, rangeLabel, loading = false }) => {
  if (loading) {
    return (
      <div className="sales-trend-chart">
        <div className="sales-trend-chart__empty">Loading trend&hellip;</div>
      </div>
    );
  }

  const hasAnyData = points.some((p) => p.sales > 0 || p.orders > 0);
  if (!hasAnyData) {
    return (
      <div className="sales-trend-chart">
        <div className="sales-trend-chart__empty">No orders yet in this window.</div>
      </div>
    );
  }

  return (
    <div className="sales-trend-chart">
      <div className="sales-trend-chart__heading">
        <h3>Sales &amp; Orders Trend</h3>
        <span className="sales-trend-chart__range">{rangeLabel}</span>
      </div>
      <div className="sales-trend-chart__panels">
        <TrendPanel
          title="Sales"
          points={points}
          metric="sales"
          colorClass="trend-panel--sales"
          formatValue={formatCurrency}
          rangeLabel={rangeLabel}
        />
        <TrendPanel
          title="Orders"
          points={points}
          metric="orders"
          colorClass="trend-panel--orders"
          formatValue={(v) => String(Math.round(v))}
          rangeLabel={rangeLabel}
        />
      </div>
    </div>
  );
};

export default SalesTrendChart;
